import { Box, Typography } from '@material-ui/core'
import { htmlToReactParser } from 'common/HTMLToReactParser'
import { JobProps, JobStatus } from 'common/types'
import { createJobSlug } from 'common/utils/createJobSlug'
import formatLocation from 'common/utils/formatLocation'
import JobApplyModal from 'components/core/modal/JobApplyModal/JobApplyModal'
import { AnalyticUserType, logEventAnalytics, useAnalytics } from 'hooks/use-analytics/useAnalytics'
import { useProfilePercentage } from 'hooks/use-profile-percentage/useProfilePercentage'
import { useScreenSizes } from 'hooks/use-screen-sizes/useScreenSizes'
import moment from 'moment'
import { useAuth } from 'pages/auth/hooks/useAuth'
import { useOnboarding } from 'pages/onboarding/hooks/useOnboarding'
import { Link, useHistory } from 'react-router-dom'
import { useTheme } from 'styled-components'
import JobApplyButton from './components/JobApplyButton'
import { JobDetailsPill } from './components/JobDetailsPill'
import { JobDetailsDescriptionWrapper } from './components/atoms/JobDetailsDescriptionWrapper/JobDetailsDescriptionWrapper'
import { useJobSalaryPillFormat } from './hooks/useJobSalaryPillFormat'
import { useMemberJobDetails } from './hooks/useMemberJobDetails'
import { StyledCopyIcon } from 'pages/content/components/posts/PostCard'
import { ReactComponent as ActionContextIcon } from 'icons/action-context-icon.svg'
import {
  ContactDetailsWrapper,
  JobDetailsContainer,
  JobDetailsItemBodyText,
  RecruiterProfileImg,
  RecruiterTitleWorkPlaceText,
  StyledAvatorAndTitleWrapper,
  StyledContentText,
  StyledJobCompanyImg,
  StyledJobDetailsText,
  StyledJobPillsWrapper,
  StyledJobTitle,
  StyledPostedJobDetailsSection,
  StyledRecruiterDetailsCard,
  StyledRecruiterHeading,
  StyledRecruiterTitle
} from './styled'
import { EllipsisButton } from 'pages/private-jobs/components/styles'
import { CustomIconContextMenu } from 'components/core/actions-menu-noborder/CustomIconContextMenu'
import { notify } from 'hooks/use-notification/useNotification'
import { Loader } from 'components/core/loader'
import { MobileBackButton } from 'common/atoms/MobileBackButton/MobileBackButton'

interface IJobDetailsProps {
  job: JobProps
  scrollDescriptionSection?: boolean
  openInNewTab?: boolean
  loading?: boolean
}

export const JobDetails = ({ job: _job, scrollDescriptionSection, openInNewTab }: IJobDetailsProps) => {
  const theme = useTheme()
  const { isDevice } = useScreenSizes()
  const { backendUser, backendPartner } = useAuth()
  const { handleTracker } = useAnalytics()

  const { job, alreadyApplied, setAlreadyApplied, user, isApplyModalOpen, setIsApplyModalOpen, loading } = useMemberJobDetails(
    _job?.id,
    _job
  )

  const toggleModal = () => {
    setIsApplyModalOpen(state => !state)
  }

  const handleJobApply = async () => {
    if (job?.jobUrl) {
      const logData = {
        'User ID': backendUser?.id,
        'Email address': backendUser?.username,
        'Partner ID': backendPartner?.id,
        'Company Name': backendPartner?.name,
        'Job ID': job?.id,
        'Job Name': job?.title,
        'Job Company': job?.companyName,
        'Account Type': backendUser?.partnerId ? 'Partner' : backendUser?.role
      }

      await handleTracker(AnalyticUserType.MEMBER, 'Job apply link clicked', logData, 'mixpanel', null, backendUser)

      job.jobUrl?.includes('http://') || job.jobUrl?.includes('https://') ? null : (job.jobUrl = 'http://' + job.jobUrl)
      window.open(job?.jobUrl, '_blank')
      return
    }
    if (isDevice) {
      history.push(`/member/jobs/apply-job/${job?.id}`)
      return
    }
    if (backendUser) {
      setIsApplyModalOpen(true)
      const logData = {
        'User ID': backendUser?.id,
        'Email address': backendUser?.username,
        'Partner ID': backendPartner?.id,
        'Company Name': backendPartner?.name,
        'Job ID': job?.id,
        'Job Name': job?.title,
        'Job Company': job?.companyName,
        'Account Type': backendUser?.partnerId ? 'Partner' : backendUser?.role
      }
      await handleTracker(AnalyticUserType.MEMBER, 'Job apply link clicked', logData, 'mixpanel', null, backendUser)
    } else {
      history.push(`/job/details/${job?.id}/apply=true`)
    }
  }

  const handleJobApplySuccess = async () => {
    setAlreadyApplied(true)
    const logData = {
      'User ID': backendUser?.id,
      'Email address': backendUser?.username,
      'Partner ID': backendPartner?.id,
      'Company Name': backendPartner?.name,
      'Job ID': job?.id,
      'Job Name': job?.title,
      'Job Company': job?.companyName,
      'Account Type': backendUser?.partnerId ? 'Partner' : backendUser?.role
    }
    await handleTracker(AnalyticUserType.MEMBER, 'Job applied', logData, 'mixpanel', null, backendUser)
  }

  const getSubQuestions = (questionText: string) => {
    if (user?.onboardingQuestions) {
      const question = user?.onboardingQuestions.find(x => x.question === questionText)
      return question?.answer
    }
    return ''
  }

  const handleCopyClipBoard = () => {
    navigator.clipboard.writeText(`https://${window.location.host}/job/${createJobSlug(job?.id, job?.title)}`)
    const partnerLog = {}
    const userLog = {}

    if (backendUser?.partnerId) {
      partnerLog['Partner ID'] = backendUser?.partnerId
      partnerLog['Company Name'] = backendPartner?.name
    }

    logEventAnalytics(
      backendUser?.partnerId ? 'Partner' : backendUser?.role,
      'Copy link to job',
      'mixpanel',
      'Copy link to job',
      {
        'Job ID': job?.id,
        'Job Name': job?.title,
        'User ID': backendUser?.id,
        'Account Type': backendUser?.partnerId ? 'Partner' : backendUser?.role,
        ...userLog,
        ...partnerLog
      },
      backendUser?.id,
      {
        $name: `${backendUser?.firstName} ${backendUser?.lastName}`,
        $email: backendUser?.username
      }
    )
    notify('success', 'Link copied to clipboard')
  }

  const { inviteUsersByEmail } = useOnboarding()
  const history = useHistory()
  const headline = getSubQuestions('Headline')
  const { getProfilePercentage } = useProfilePercentage()
  const { formatSalary } = useJobSalaryPillFormat(job)

  const showApplyButton = !backendUser?.partnerId || backendUser?.partnerId !== job?.partnerId

  const handleClickButton = () => {
    history.goBack()
  }

  const jobExpired = moment(job?.expiryDate) < moment(Date.now()) || job?.status === JobStatus.CLOSED

  return (
    <>
      {loading || !job ? (
        <Box width="100%" height="100%" display="flex" justifyContent="center" alignItems="center">
          <Loader />
        </Box>
      ) : (
        <JobDetailsContainer isDevice={isDevice}>
          { isDevice && (
            <Box width="100%" display="grid" gridTemplateColumns="0.5fr 1fr 0.5fr" gridGap="4rem" mb="1.375rem">
              <Box display="flex" alignItems="center" justifySelf="left">
                <MobileBackButton style={{ padding: '0' }} onClick={handleClickButton} />
              </Box>
              <Box textAlign="center" display="flex" alignItems="center">
                <Typography variant='body1' noWrap style={{ fontWeight: 700 }}> 
                  Job Details
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" justifyContent="flex-end">
                <EllipsisButton>
                  <CustomIconContextMenu
                    anchorOriginHorizontal={'right'}
                    noPadding={true}
                    customItemPadding={'0rem 1.125rem'}
                    title=""
                    icon={<ActionContextIcon fill={theme?.colors?.accent} />}
                  >
                    <Box onClick={handleCopyClipBoard}>
                      <StyledCopyIcon />
                      <span style={{ marginLeft: 8 }}>Copy Link</span>
                    </Box>
                  </CustomIconContextMenu>
                </EllipsisButton>
              </Box>
            </Box>
          )}
          <StyledPostedJobDetailsSection
            isDevice={isDevice}
            borderBottom={`1px solid ${theme.colors.supplementary1}`}
            bgcolor={theme.colors.supplementary}
            paddingTop={isDevice ? 0 : '1.333rem !important'}
            borderRadius="1.875rem 1.875rem 0 0"
          >
            <Box width="100%">
              <StyledAvatorAndTitleWrapper isDevice={isDevice} width="100%">
                <Box zIndex={2}>
                  <StyledJobCompanyImg
                    src={job?.companyLogo}
                    onClick={() => history.push(`/company/${job?.partnerId}`)}
                    isDevice={isDevice}
                  />
                </Box>
                <Box display="grid" gridTemplateColumns={isDevice ? '13fr 1fr' : 'auto'}>
                  <Box>
                    <Link
                      to={`/job/${createJobSlug(job?.id, job?.title)}`}
                      onClick={evt => {
                        if (openInNewTab) {
                          evt.preventDefault()
                          window.open(`/job/${createJobSlug(job?.id, job?.title)}`, '_blank')
                        }
                      }}
                    >
                      <StyledJobTitle
                        isDevice={isDevice}
                        variant={isDevice ? 'subtitle2' : 'h3'}
                        component='h1'
                      >
                        {job?.title}
                      </StyledJobTitle>
                    </Link>
                    <StyledJobDetailsText
                      isDevice={isDevice}
                      theme={theme}
                      variant={'body2'}
                      style={{ cursor: 'pointer', color: theme.colors.accent }}
                    >
                      <Box onClick={() => history.push(`/company/${job?.partnerId}`)}>{job?.companyName}</Box>
                    </StyledJobDetailsText>
                    {isDevice && (
                      <Typography variant="caption">
                        <Box>{moment(job?.createdAt).fromNow()}</Box>
                      </Typography>
                    )}
                  </Box>
                </Box>
              </StyledAvatorAndTitleWrapper>
              <StyledJobPillsWrapper isDevice={isDevice}>
                {job?.jobType && <JobDetailsPill job={job} text={job?.jobType} />}
                {job?.workplace && <JobDetailsPill job={job} text={job?.workplace} />}
                {job?.location != '' && (
                  <JobDetailsPill
                    justifyContent="center"
                    mb="0.34rem"
                    mr="0.03rem"
                    job={job}
                    text={formatLocation(job?.location)}
                  />
                )}
                {formatSalary != '' && (
                  <JobDetailsPill justifyContent="center" mb="0.34rem" mr="0.03rem" job={job} text={formatSalary} />
                )}
              </StyledJobPillsWrapper>
              <Box display="flex" justifyContent="start" pb={ isDevice ? "0" : "1rem"} width={isDevice ? '100%' : '86%'} mb={ isDevice ? "1.5rem" : "0"}>
                <JobApplyButton
                  alreadyApplied={alreadyApplied}
                  handleJobApply={handleJobApply}
                  job={job}
                  showAlertBox={true}
                />
              </Box>
            </Box>
          </StyledPostedJobDetailsSection>
          <Box
            overflow={scrollDescriptionSection ? 'auto' : 'visible'}
            maxHeight={scrollDescriptionSection ? '60vh' : 'unset'}
            paddingBottom="5rem !important"
          >
            <Box color={theme.colors.accent1} className="ql-editor" padding="0.875rem 0 0 0 !important">
              {job?.jobDescription && (
                <JobDetailsItemBodyText isDevice={isDevice}>
                  <JobDetailsDescriptionWrapper isDevice={isDevice}>
                    {htmlToReactParser.parse(job?.jobDescription)}
                  </JobDetailsDescriptionWrapper>
                </JobDetailsItemBodyText>
              )}
            </Box>
            {job?.userId && (
              <ContactDetailsWrapper>
                <Box>
                  <StyledRecruiterHeading variant={isDevice ? 'subtitle1' : 'h6'} isDevice={isDevice}>
                    Meet the recruiter
                  </StyledRecruiterHeading>
                  <StyledRecruiterDetailsCard isDevice={isDevice}>
                    <Box zIndex={2}>
                      <RecruiterProfileImg
                        src={user?.image}
                        onClick={() => history.push(backendUser?.id === user?.id ? '/profile' : `/profile/${user?.id}`)}
                        style={{
                          cursor: 'pointer'
                        }}
                        isDevice={isDevice}
                      />
                    </Box>
                    <Box zIndex={2}>
                      <StyledContentText isDevice={isDevice}>
                        <StyledRecruiterTitle
                          isDevice={isDevice}
                          variant={isDevice ? 'subtitle2' : 'h6'}
                          onClick={() => history.push(`/profile/${user?.id}`)}
                          style={{
                            cursor: 'pointer'
                          }}
                        >
                          {user?.firstName} {user?.lastName}
                        </StyledRecruiterTitle>
                        <RecruiterTitleWorkPlaceText
                          variant={isDevice ? 'subtitle2' : 'h6'}
                          isDevice={isDevice}
                          theme={theme}
                        >
                          {headline || ''}
                        </RecruiterTitleWorkPlaceText>
                      </StyledContentText>
                    </Box>
                  </StyledRecruiterDetailsCard>
                </Box>
              </ContactDetailsWrapper>
            )}
          </Box>
        </JobDetailsContainer>
      )}
      <JobApplyModal
        onSuccess={handleJobApplySuccess}
        isOpen={isApplyModalOpen}
        close={toggleModal}
        jobId={job?.id}
        title={job?.title}
        companyLogo={job?.companyLogo}
        companyName={job?.companyName}
        companyLocation={job?.location}
      />
    </>
  )
}
